<!-- @components/Spotlight.svelte -->
<script>
  import { onMount } from 'svelte';
  import { fromEvent } from 'rxjs';
  import { debounceTime, map, distinctUntilChanged, filter } from 'rxjs/operators';
  import { fade } from 'svelte/transition';
  import { fadeScale } from './transitions.js';
  import { Search } from 'lucide-svelte';
  import * as Lucide from 'lucide-svelte';

  /* ===== PROPS ===== */
  export let open = false;
  export let items = []; // menu list
  export let onSelect = () => {}; // callback(item)

  let inputRef;
  let results = [];
  let activeIndex = 0;
  let subscription;

  // function closeGlobalSpotlight() {
  //   window.dispatchEvent(new CustomEvent('close-spotlight'));
  // }

  const closeHandler = () => {
    closeSpotlight();
  };

  function openSpotlight() {
    open = true;
    results = [...items];
    activeIndex = 0;
    setTimeout(() => inputRef?.focus(), 0);
  }

  function closeSpotlight() {
    open = false;
    results = [];
    activeIndex = 0;
    if (inputRef) inputRef.value = '';
  }

  function filterItems(query) {
    if (!query) {
      results = [...items];
      activeIndex = 0;
      return;
    }

    const q = query.toLowerCase();
    results = items.filter((item) => (item.label + ' ' + item.keywords.join(' ')).toLowerCase().includes(q));
    activeIndex = 0;
  }

  function select(item) {
    onSelect(item);
    closeSpotlight();
  }

  function handleKey(e) {
    if (!results.length) return;

    if (e.key === 'ArrowDown') {
      activeIndex = (activeIndex + 1) % results.length;
    }

    if (e.key === 'ArrowUp') {
      activeIndex = (activeIndex - 1 + results.length) % results.length;
    }

    if (e.key === 'Enter') {
      select(results[activeIndex]);
    }
  }

  // onMount(() => {
  //   const keyHandler = (e) => {
  //     if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
  //       e.preventDefault();
  //       openSpotlight();
  //     }
  //     if (e.key === 'Escape') {
  //       closeSpotlight();
  //     }
  //   };

  //   window.addEventListener('keydown', keyHandler);

  //   subscription = fromEvent(document, 'input')
  //     .pipe(
  //       map((e) => e.target),
  //       filter((el) => el === inputRef),
  //       map((el) => el.value.trim()),
  //       debounceTime(300),
  //       distinctUntilChanged()
  //     )
  //     .subscribe(filterItems);

  //   onDestroy(() => {
  //     window.removeEventListener('keydown', keyHandler);
  //     subscription?.unsubscribe();
  //   });
  // });

  onMount(() => {
    const keyHandler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        openSpotlight();
      }

      if (e.key === 'Escape') {
        closeSpotlight();
      }
    };

    const spotlightHandler = () => {
      openSpotlight();
    };

    window.addEventListener('keydown', keyHandler);

    window.addEventListener('open-spotlight', spotlightHandler);

    window.addEventListener('close-spotlight', closeHandler);

    subscription = fromEvent(document, 'input')
      .pipe(
        map((e) => e.target),
        filter((el) => el === inputRef),
        map((el) => el.value.trim()),
        debounceTime(300),
        distinctUntilChanged()
      )
      .subscribe(filterItems);

    return () => {
      window.removeEventListener('keydown', keyHandler);

      window.removeEventListener('open-spotlight', spotlightHandler);

      window.removeEventListener('close-spotlight', closeHandler);

      subscription?.unsubscribe();
    };
  });

  function formatHotkey(hotkey) {
    if (!hotkey) return '';

    return hotkey
      .replace('ctrl+', '⌘ ')
      .replace('shift+', '⇧ ')
      .replace('alt+', '⌥ ')
      .replace('escape', 'ESC')
      .toUpperCase();
  }
</script>

{#if open}
  <!-- Overlay -->
  <div class="fixed inset-0 z-40 bg-black/20 backdrop-blur-sm" on:click={closeSpotlight} transition:fade></div>

  <!-- Spotlight -->
  <div
    class="fixed top-[18%] left-1/2 -translate-x-1/2 z-50
         w-full max-w-lg
         bg-white
         rounded-2xl
         overflow-hidden
         shadow-2xl
         border border-gray-300"
    transition:fadeScale={{ duration: 250 }}
    on:click|stopPropagation>
    <!-- Input -->
    <div class="flex items-center gap-3 px-4 py-3 border-b border-gray-200">
      <Search size="18" class="text-gray-400" />

      <input
        bind:this={inputRef}
        on:keydown={handleKey}
        placeholder="Search menu…"
        class="flex-1 bg-transparent outline-none text-sm" />

      <kbd
        class="hidden sm:inline-flex
               rounded border border-gray-300
               px-2 py-1 text-xs text-gray-400">
        ⌘K
      </kbd>
    </div>

    <!-- Results -->
    {#if results.length}
      <ul class="max-h-64 overflow-y-auto">
        {#each results as item, i}
          <li
            class="flex items-center justify-between px-4 py-2 mx-2 rounded-lg cursor-pointer
         {i === activeIndex ? 'bg-gray-100' : 'hover:bg-gray-100'}"
            on:click={() => select(item)}>
            <div class="flex items-center gap-3">
              <svelte:component this={Lucide[item.icon] || Lucide.CircleQuestionMark} size="16" class="text-gray-500" />

              <div class="flex flex-col">
                <span class="text-sm font-medium">
                  {item.label}
                </span>
              </div>
            </div>

            {#if item.hotkey}
              <kbd
                class="inline-flex items-center
             rounded-md
             border border-gray-300
             bg-white
             px-2 py-1
             text-[11px]
             font-medium
             text-gray-500
             shadow-sm">
                {formatHotkey(item.hotkey)}
              </kbd>
            {/if}
          </li>
        {/each}
      </ul>
    {:else}
      <div class="py-8 text-center text-sm text-gray-400">Không tìm thấy menu</div>
    {/if}
  </div>
{/if}
